import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
    Container
} from "@mui/material";
import {useNavigate} from "react-router-dom";
import { AutoAwesome, EmojiObjectsOutlined, InsightsOutlined} from "@mui/icons-material";
import axios from "axios";
import {backendURL} from "../assets/constants";

export const Home = ({user, setUser}) => {
    const navigate = useNavigate();
    const [value, setValue] = useState(user ? (user.user_id || user._id || '') : '');
    const [loading, setLoading] = useState(false);
    const [tipIndex, setTipIndex] = useState(0);
    const [infoOpen, setInfoOpen] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogPath, setDialogPath] = useState('');

    const rotatingTips = useMemo(() => [
        'Notice the exact words in your thought—small shifts can change the tone.',
        'Speak to yourself like you would to someone you care about.',
        'Look for honest evidence that supports a kinder interpretation.',
        'Plan one compassionate action you can take after this session.'
    ], []);

    useEffect(() => {
        if (user) {
            setValue(user.user_id || user._id || '');
        }
    }, [user]);

    useEffect(() => {
        const interval = window.setInterval(() => {
            setTipIndex((prev) => (prev + 1) % rotatingTips.length);
        }, 8000);
        return () => window.clearInterval(interval);
    }, [rotatingTips.length]);

    const onChange = (event) => {
        setValue(event.target.value);
    };

    const createUser = async (path) => {
        const trimmedValue = value?.toString().trim();
        if (!trimmedValue) {
            alert('Please enter a user id to continue.');
            return;
        }
        setLoading(true);
        try {
            const response = await axios.post(`${backendURL}/createUser`, {user_id: trimmedValue});
            if (response?.data) {
                setUser(response.data);
            }
            navigate(path);
        } catch (error) {
            console.error('Falling back to offline mode. Unable to create user via backend.', error);
            const offlineId = trimmedValue || `offline-${Date.now()}`;
            const offlineUser = {
                _id: offlineId,
                user_id: offlineId,
                isOfflineUser: true
            };
            setUser(offlineUser);
            alert('Could not reach the server, continuing in offline mode. Your answers will be kept locally.');
            navigate(path);
        } finally {
            setLoading(false);
        }
    };


    const isOffline = Boolean(user?.isOfflineUser);

    const highlightItems = useMemo(() => ([
        {
            icon: <AutoAwesome color="primary" fontSize="small" />,
            title: 'Guided prompts',
            description: 'Move through four gentle reflections to soften a tough thought.'
        },
        {
            icon: <InsightsOutlined color="secondary" fontSize="small" />,
            title: 'Hopeful rewrites',
            description: 'Practice turning negative language into kinder, realistic alternatives.'
        },
        {
            icon: <EmojiObjectsOutlined color="success" fontSize="small" />,
            title: 'Quick reset',
            description: 'Drop in anytime for a five-minute mental refresh when you need it most.'
        }
    ]), []);

    const renderLoadingState = () => (
        <Box className="spinner-container" sx={{flexDirection: 'column'}}>
            <CircularProgress color="primary" thickness={4} size={56} sx={{mb: 2}} />
            <Typography variant="body1" color="text.secondary">
                Preparing your session...
            </Typography>
        </Box>
    );

    const renderHeroCard = (showActions = true) => (
        <Paper elevation={0} sx={{p: {xs: 4, md: 6}, position: 'relative', overflow: 'hidden', textAlign: 'left'}}>
            <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack spacing={1} alignItems="flex-start">
                        <Chip
                            label={isOffline ? 'Offline practice' : 'Your reflection space'}
                            color={isOffline ? 'secondary' : 'primary'}
                            variant="outlined"
                            sx={{fontWeight: 600}}
                        />
                        <Typography variant="h3" sx={{fontWeight: 800, lineHeight: 1.05}}>
                            Cognitive Reframing
                        </Typography>
                    </Stack>

                </Stack>
                <Typography variant="body1" color="text.secondary" sx={{maxWidth: 860, lineHeight: 1.7}}>
                    We’re exploring how human-centered computing can support students managing rumination,
                    burnout, and anxiety by guiding short, compassionate reframing exercises that fit into busy days.
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{maxWidth: 860, lineHeight: 1.7}}>
                    Spot thinking traps, experiment with kinder wording, and leave with a hopeful next step.
                    Everything you write stays yours—share only when you feel ready.
                </Typography>
                {showActions && (
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems="center">
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => {
                                setDialogPath('/overcoming-negative-thoughts');
                                setOpenDialog(true);
                            }}
                        >
                            Start reframing
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={() => {
                                setDialogPath('/changing-thoughts-with-an-ai-assistant');
                                setOpenDialog(true);
                            }}
                        >
                            Try the AI companion
                        </Button>
                    </Stack>
                )}
            </Stack>
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    background: 'radial-gradient(circle at 25% 25%, rgba(31,111,235,0.18), transparent 60%)'
                }}
            />
        </Paper>
    );


    const renderHighlights = () => (
        <Grid container spacing={3}>
            {highlightItems.map((item, idx) => (
                <Grid item xs={12} md={4} key={idx}>
                    <Paper elevation={0} sx={{p: 3, height: '100%'}}>
                        <Stack spacing={1.5} alignItems="flex-start">
                            <Stack direction="row" spacing={1} alignItems="center">
                                {item.icon}
                                <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                                    {item.title}
                                </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                {item.description}
                            </Typography>
                        </Stack>
                    </Paper>
                </Grid>
            ))}
        </Grid>
    );

    const renderReflectionTip = () => (
        <Paper elevation={0} sx={{p: 3}}>
            <Stack spacing={1.5} alignItems="flex-start">
                <Typography variant="subtitle2" color="text.secondary" sx={{letterSpacing: '.08em'}}>
                    REFLECTION TIP
                </Typography>
                <Typography variant="body1" sx={{fontStyle: 'italic'}}>
                    “{rotatingTips[tipIndex]}”
                </Typography>
                <Button variant="text" size="small" onClick={() => setTipIndex((tipIndex + 1) % rotatingTips.length)}>
                    Show another tip
                </Button>
            </Stack>
        </Paper>
    );

    const infoDialog = (
        <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Cognitive Reframing</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body1">
                        Our research studies how human-centered computing can ease rumination, burnout, and anxiety
                        for students by delivering quick, compassionate reframing exercises on demand.
                    </Typography>
                    <Typography variant="body1">
                        Your reflections stay on this device unless you choose to share them, giving you space to
                        practice kinder self-talk while we explore supportive interaction patterns.
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setInfoOpen(false)} autoFocus>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );

    const userIdDialog = (
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Continue with User ID</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    Please enter a user ID to continue. This will allow you to save your progress and access it later.
                </Typography>
                <TextField
                    autoFocus
                    margin="dense"
                    id="user-id"
                    label="User ID"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={value}
                    onChange={onChange}
                    helperText="Enter a unique ID (e.g., your email or a username)"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                <Button onClick={() => {
                    setOpenDialog(false);
                    createUser(dialogPath);
                }}>Continue</Button>
            </DialogActions>
        </Dialog>
    );

    if (loading) {
        return (
            <>
                {renderLoadingState()}
                {infoDialog}
                {userIdDialog}
            </>
        );
    }

    if (user) {
        return (
            <Container maxWidth="md" sx={{pt: 0, pb: 4}}>
                <Stack spacing={4}>
                    {renderHeroCard(true)}
                    {renderHighlights()}
                    {renderReflectionTip()}
                </Stack>
                {infoDialog}
                {userIdDialog}
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{pt: 0, pb: 4}}>
            <Stack spacing={4}>
                {renderHeroCard(true)}
                {renderHighlights()}
                {renderReflectionTip()}
            </Stack>
            {infoDialog}
            {userIdDialog}
        </Container>
    );
};
